import { Component, Directive, HostBinding, Input } from "@angular/core";
import { Constructor } from "@angular/material/core/common-behaviors/constructor";

export type BorderRadius =
  | 0
  // rem
  | 0.5
  | 1
  | 1.5
  // px
  | 2
  | 4
  | 6
  // either rem or px
  | 9999
  | "none"
  | "small"
  | "default"
  | "large"
  | "round";

export interface DimensionBorderRadii {
  topLeft?: BorderRadius;
  topRight?: BorderRadius;
  bottomLeft?: BorderRadius;
  bottomRight?: BorderRadius;
}

// todo: figure out return type for mixins
// tslint:disable-next-line:typedef
export function WithBorderRadius<Base extends Constructor<Component>>(
  BaseClass: Base
) {
  @Directive()
  // tslint:disable-next-line:class-name
  class _WithBorderRadius extends BaseClass {
    @Input()
    borderRadius?: BorderRadius | DimensionBorderRadii;

    protected get borderRadiusClassList(): string[] {
      return borderRadiusToClassList(this.borderRadius);
    }
  }

  return _WithBorderRadius;
}

@Component({
  selector: "exp-border-radius-container, [exp-border-radius-container]",
  template: "<ng-content></ng-content>"
})
export class BorderRadiusContainerComponent extends WithBorderRadius(class {}) {
  @HostBinding("class")
  get hostClass(): string {
    return this.borderRadiusClassList
      .filter((className) => className != null)
      .join(" ");
  }
}

function borderRadiusToClassList(
  borderRadius?: BorderRadius | DimensionBorderRadii
): string[] {
  if (borderRadius == null) {
    return [];
  }

  return typeof borderRadius === "number" ||
    (typeof borderRadius === "string" && !isNaN(Number(borderRadius)))
    ? borderRadiusToClassList(numericBorderRadiusToString(Number(borderRadius)))
    : typeof borderRadius === "string"
    ? [stringBorderRadiusToClass(borderRadius)]
    : isDimensionBorderRadii(borderRadius)
    ? borderRadiusDimensionsToClassList(borderRadius)
    : ["br--default"];
}

function isDimensionBorderRadii(
  borderRadius: any
): borderRadius is DimensionBorderRadii {
  const goodKeys = new Set([
    "topLeft",
    "topRight",
    "bottomLeft",
    "bottomRight"
  ]);

  const isValid =
    borderRadius != null &&
    typeof borderRadius === "object" &&
    !Array.isArray(borderRadius) &&
    Object.keys(borderRadius).every((key) => goodKeys.has(key));

  if (!isValid) {
    warnUnsupportedBorderRadius(borderRadius);
  }

  return isValid;
}

function numericBorderRadiusToString(borderRadius: number): BorderRadius {
  switch (borderRadius) {
    case 0:
      return "none";
    case 0.5:
    case 2:
      return "small";
    case 1:
    case 4:
      return "default";
    case 1.5:
    case 6:
      return "large";
    case 9999:
      return "round";
    default:
      warnUnsupportedBorderRadius(borderRadius);
      return "default";
  }
}

function stringBorderRadiusToClass(borderRadius: string): string {
  switch (borderRadius) {
    case "none":
      return "br--none";
    case "small":
      return "br--small";
    case "default":
      return "br--default";
    case "large":
      return "br--large";
    case "round":
      return "br--round";
    default:
      warnUnsupportedBorderRadius(borderRadius);
      return "br--default";
  }
}

function warnUnsupportedBorderRadius(borderRadius: any): void {
  let stringifiedBr: string;

  try {
    stringifiedBr =
      typeof borderRadius === "object"
        ? JSON.stringify(borderRadius)
        : String(borderRadius);
  } catch (e) {
    stringifiedBr = String({}); // [object Object]
  }

  // tslint:disable-next-line:no-console
  console.warn("Unsupported border radius: " + stringifiedBr);
}

function borderRadiusDimensionsToClassList(
  borderRadius: DimensionBorderRadii
): string[] {
  return (
    Object.entries(borderRadius) as Array<
      [keyof DimensionBorderRadii, BorderRadius]
    >
  ).map(([dimension, value]) =>
    borderRadiusDimensionToClass(dimension as keyof DimensionBorderRadii, value)
  );
}

function borderRadiusDimensionToClass(
  dimension: keyof DimensionBorderRadii,
  borderRadius?: BorderRadius
): string {
  if (typeof borderRadius === "number") {
    return borderRadiusDimensionToClass(
      dimension,
      numericBorderRadiusToString(borderRadius)
    );
  }

  return `br--${String(dimension)}-${borderRadius}`;
}
